import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  Layers,
  ShieldCheck,
  Workflow,
  Database,
  Mail,
  Inbox,
  LayoutDashboard,
  Sparkles,
  Lightbulb,
  Activity,
  Globe,
  CalendarCheck,
  MessageSquareQuote,
  TrendingUp,
  Anchor,
  Plane,
  Building2,
  Map,
  Star,
  Sailboat,
  Gem,
  Briefcase,
  Clock3,
  Eye,
  HeartHandshake,
  Network,
  Users,
  GraduationCap,
  BarChart3,
  ClipboardList,
  Route,
  Utensils,
  Dumbbell,
  Stethoscope,
  Home as HomeIcon,
} from "lucide-react";
import EnquiryForm from "../components/EnquiryForm";

const SECTORS = [
  { i: Sailboat, t: "Yacht charter" },
  { i: Plane, t: "Luxury travel" },
  { i: Building2, t: "Boutique hospitality" },
  { i: Map, t: "Tourism operators" },
  { i: Star, t: "Private experiences" },
  { i: Anchor, t: "Destination brands" },
  { i: HeartHandshake, t: "Concierge services" },
  { i: Briefcase, t: "High-value service businesses" },
];

const SYSTEMS = [
  {
    i: Globe,
    t: "Premium Websites & Digital Presence",
    d: "Calm, credible, conversion-aware websites that reflect the quality of the service being delivered.",
  },
  {
    i: Database,
    t: "CRM & Customer Journey Systems",
    d: "Considered CRM design that connects enquiries, bookings, communication, and aftercare into one source of truth.",
  },
  {
    i: Workflow,
    t: "Automation & Workflow Infrastructure",
    d: "Quiet automation that removes manual handoffs, accelerates responses, and gives teams operational clarity.",
  },
  {
    i: LayoutDashboard,
    t: "Bespoke Apps & Client Portals",
    d: "Purpose-built platforms, internal dashboards, and guest-facing portals — designed around how your business actually works.",
  },
  {
    i: Eye,
    t: "Digital Discoverability",
    d: "Premium presence across search and AI assistants — built around editorial substance, not keyword volume.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* 2. HERO */}
      <Hero />

      {/* 3. POSITIONING */}
      <Positioning />

      {/* 4. APPROACH */}
      <Approach />

      {/* 5. SYSTEMS */}
      <Systems />

      {/* 6. SECTORS */}
      <Sectors />

      {/* 7. PROOF / Featured Projects */}
      <Proof />

      {/* 7a. AFFORDABILITY TEASER — "Think Custom Software Is Expensive?" */}
      <AffordabilityTeaser />

      {/* 7b. FRANCHISE INFRASTRUCTURE */}
      <FranchiseInfrastructure />

      {/* 7c. HOSPITALITY OPERATIONS & SYSTEMS */}
      <HospitalityOperations />

      {/* 8. WHY IT MATTERS */}
      <WhyItMatters />

      {/* ENQUIRY FORM */}
      <EnquiryForm />

      {/* 9. CTA */}
      <FinalCta />
    </div>
  );
}

/* -------------------- HERO -------------------- */
function Hero() {
  return (
    <section className="relative pt-36 sm:pt-44 pb-24 sm:pb-32">
      <HeroAmbient />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold reveal">
              Digital infrastructure · operational systems
            </span>
            <h1 className="mt-7 font-display text-[40px] sm:text-6xl lg:text-7xl xl:text-[78px] leading-[1.03] tracking-tight text-mute-100 reveal reveal-delay-1">
              The invisible digital layer behind{" "}
              <em className="not-italic text-gradient-gold">exceptional customer experiences</em>.
            </h1>
            <p className="mt-7 text-[17px] sm:text-[19px] leading-[1.6] text-mute-200 max-w-2xl reveal reveal-delay-2">
              Linked2Marketing designs the bespoke systems, automation, and customer journeys
              behind hospitality, tourism, yacht charter, and experience-led brands — quietly
              connecting websites, CRM, communication, and operations into one calm, considered layer.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 reveal reveal-delay-3">
              <Link
                to="/#enquiry"
                data-testid="hero-cta-book"
                className="btn-primary"
              >
                Start a conversation <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href="#approach" className="btn-secondary" data-testid="hero-cta-explore">
                Explore our approach
              </a>
            </div>
            <p className="mt-8 text-[13px] text-mute-300 max-w-lg reveal reveal-delay-4">
              For premium service brands where every enquiry, booking, and guest interaction matters.
            </p>
          </div>
          <div className="lg:col-span-5 reveal reveal-delay-2">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroAmbient() {
  return (
    <div className="absolute inset-0 -z-10">
      <div
        className="absolute right-[-8%] top-[-10%] w-[680px] h-[680px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.16), transparent 70%)",
        }}
      />
      <div
        className="absolute left-[-10%] bottom-[-12%] w-[560px] h-[560px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.08), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 grid-bg opacity-[0.22] mask-fade-y" />
    </div>
  );
}

function HeroVisual() {
  return (
    <div data-testid="hero-visual" className="relative h-[500px] sm:h-[560px] w-full">
      {/* Connection diagram */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 540 560"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="lineG" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#C7A66A" stopOpacity="0.55" />
            <stop offset="1" stopColor="#C7A66A" stopOpacity="0.15" />
          </linearGradient>
        </defs>
        <path
          d="M 90 110 C 170 160, 220 200, 320 200"
          stroke="url(#lineG)"
          strokeWidth="1"
          strokeDasharray="2 6"
          className="animate-pulse-line"
        />
        <path
          d="M 320 200 C 380 280, 360 380, 250 420"
          stroke="url(#lineG)"
          strokeWidth="1"
          strokeDasharray="2 6"
          className="animate-pulse-line"
        />
        <path
          d="M 250 420 C 170 460, 110 380, 90 110"
          stroke="url(#lineG)"
          strokeWidth="1"
          strokeDasharray="2 6"
          className="animate-pulse-line"
        />
        <path
          d="M 320 200 C 440 240, 460 420, 380 500"
          stroke="url(#lineG)"
          strokeWidth="1"
          strokeDasharray="2 6"
          className="animate-pulse-line"
        />
      </svg>

      <FloatCard style={{ top: "2%", left: "0%" }} className="w-[260px] animate-float-slow" label="Enquiry">
        <Row k="Source" v="Direct website" />
        <Row k="Type" v="Yacht charter · 5 days" />
        <Row k="Guests" v="6" />
        <div className="mt-3 text-[11px] text-gold tracking-[0.18em] uppercase">
          Auto-routed · 2m response
        </div>
      </FloatCard>

      <FloatCard
        style={{ top: "10%", right: "0%" }}
        className="w-[240px] animate-float"
        label="Guest journey"
      >
        <ol className="mt-1 space-y-1.5 text-[11.5px] text-mute-200">
          {["Discover", "Enquire", "Tailor", "Confirm", "Onboard", "Experience", "Aftercare"].map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span
                className={`w-1.5 h-1.5 rounded-full ${i < 3 ? "bg-gold" : "bg-mute-300/60"}`}
              />
              <span className={i < 3 ? "text-mute-100" : ""}>{s}</span>
            </li>
          ))}
        </ol>
      </FloatCard>

      <FloatCard
        style={{ top: "44%", left: "16%" }}
        className="w-[260px] animate-float"
        label="CRM · pipeline"
      >
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { l: "New", v: 8 },
            { l: "Qual.", v: 5 },
            { l: "Prop.", v: 3 },
            { l: "Won", v: 2 },
          ].map((s) => (
            <div
              key={s.l}
              className="rounded-md bg-white/[0.025] border border-white/5 p-2"
            >
              <div className="text-[9px] uppercase tracking-wider text-mute-300">
                {s.l}
              </div>
              <div className="mt-1 text-sm tabular-nums text-mute-100">{s.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-mute-300">
          18 active enquiries · 4 awaiting reply
        </div>
      </FloatCard>

      <FloatCard
        style={{ bottom: "4%", left: "2%" }}
        className="w-[230px] animate-float-slow"
        label="Communication"
      >
        <div className="flex items-center gap-2 text-[12px] text-mute-100">
          <CalendarCheck className="w-3.5 h-3.5 text-gold" />
          Confirmation · scheduled
        </div>
        <div className="mt-1.5 text-[11px] text-mute-300">
          Personalised handover email · 14:30 today
        </div>
      </FloatCard>

      <FloatCard
        style={{ bottom: "10%", right: "2%" }}
        className="w-[230px] animate-float"
        label="Operational visibility"
      >
        <div className="flex items-end gap-1 h-10">
          {[18, 30, 24, 42, 38, 52, 60, 74, 82].map((h, i) => (
            <div
              key={i}
              className="w-2 rounded-sm"
              style={{
                height: `${h}%`,
                background:
                  "linear-gradient(180deg,#C7A66A,rgba(199,166,106,0.15))",
              }}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-mute-200">
          <span>Response time</span>
          <span className="text-mute-100 tabular-nums">−42%</span>
        </div>
      </FloatCard>
    </div>
  );
}

function FloatCard({ children, className = "", style, label }) {
  return (
    <div className={`absolute glass rounded-2xl p-4 sm:p-5 ${className}`} style={style}>
      {label && (
        <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-mute-300">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[11.5px]">
      <span className="text-mute-300">{k}</span>
      <span className="text-mute-100">{v}</span>
    </div>
  );
}

/* -------------------- POSITIONING -------------------- */
function Positioning() {
  const cards = [
    {
      icon: Compass,
      title: "Guest journey architecture",
      body: "We shape the digital path from first enquiry to post-experience follow-up, making each interaction feel considered and connected.",
    },
    {
      icon: Layers,
      title: "Operational systems",
      body: "We design the tools, workflows, automations, and CRM structures that support high-value customer relationships behind the scenes.",
    },
    {
      icon: ShieldCheck,
      title: "Premium digital presence",
      body: "We create calm, credible, conversion-aware websites that reflect the quality of the service being delivered.",
    },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-12 sm:mt-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="eyebrow text-gold">Positioning</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            Designed for brands where service quality depends on operational clarity.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-3">
          <p className="text-[16.5px] leading-[1.75] text-mute-200">
            Premium customer experiences are not created by isolated websites, disconnected CRMs,
            manual follow-ups, or fragmented booking processes. They depend on invisible systems
            that help teams respond faster, manage enquiries better, personalise communication, and
            deliver a more consistent guest journey.
          </p>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
        {cards.map((c) => (
          <article key={c.title} className="rounded-2xl glass card-hover p-7 sm:p-8">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 bg-white/[0.025] text-gold">
              <c.icon className="w-4.5 h-4.5" />
            </span>
            <h3 className="mt-6 font-display text-2xl tracking-tight text-mute-100">
              {c.title}
            </h3>
            <p className="mt-3 text-[14.5px] leading-[1.7] text-mute-200">{c.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* -------------------- APPROACH -------------------- */
function Approach() {
  const steps = [
    {
      t: "Understand the guest journey",
      d: "Map how customers discover, enquire, book, communicate, and return.",
    },
    {
      t: "Identify operational friction",
      d: "Find where manual processes, disconnected tools, or unclear messaging weaken the experience.",
    },
    {
      t: "Design the digital layer",
      d: "Build websites, systems, automations, CRM structures, and communication flows that work together.",
    },
    {
      t: "Refine for confidence",
      d: "Improve clarity, consistency, response quality, and operational control over time.",
    },
  ];
  return (
    <section id="approach" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36 scroll-mt-24">
      <div className="max-w-3xl">
        <span className="eyebrow text-gold">Approach</span>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
          A quieter, more intelligent way to build digital growth.
        </h2>
        <p className="mt-6 text-[16.5px] leading-[1.75] text-mute-200">
          Linked2Marketing does not treat marketing, websites, CRM, and automation as separate
          pieces. We design them as one connected operating layer for premium service businesses.
        </p>
      </div>

      <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {steps.map((s, i) => (
          <li key={s.t} className="relative rounded-2xl glass p-7 sm:p-8 card-hover">
            <div className="text-[11px] tabular-nums uppercase tracking-[0.24em] text-gold">
              Step 0{i + 1}
            </div>
            <h3 className="mt-4 font-display text-[26px] sm:text-3xl tracking-tight text-mute-100">
              {s.t}
            </h3>
            <p className="mt-4 text-[14.5px] leading-[1.7] text-mute-200">{s.d}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* -------------------- WHAT WE DESIGN -------------------- */
function Systems() {
  return (
    <section id="systems" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36 scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <span className="eyebrow text-gold">What we design</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            The digital infrastructure behind a refined operation.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-[15.5px] leading-[1.75] text-mute-200">
            Five connected disciplines, designed to work as one layer — so the experience your team
            delivers feels effortless to the people you serve.
          </p>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SYSTEMS.map((s, i) => (
          <article
            key={s.t}
            data-testid={`design-card-${i}`}
            className={`group relative rounded-3xl glass card-hover p-7 sm:p-8 overflow-hidden ${
              i === 4 ? "md:col-span-2 lg:col-span-1" : ""
            }`}
          >
            <div
              className="absolute -top-20 -right-20 w-[260px] h-[260px] rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
              }}
            />
            <div className="relative">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-gold/25 bg-gold/[0.05] text-gold transition-transform duration-500 group-hover:-translate-y-0.5">
                <s.i className="w-[18px] h-[18px]" />
              </span>
              <div className="mt-7 flex items-baseline gap-3">
                <span className="text-[10.5px] tabular-nums tracking-[0.24em] text-gold/80">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="h-px w-8 bg-gold/30" />
              </div>
              <h3 className="mt-3 font-display text-[22px] sm:text-[24px] leading-[1.2] text-mute-100">
                {s.t}
              </h3>
              <p className="mt-4 text-[14.5px] leading-[1.7] text-mute-200">{s.d}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* -------------------- SECTORS -------------------- */
function Sectors() {
  return (
    <section id="sectors" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36 scroll-mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="eyebrow text-gold">Sectors</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            Built for premium service environments.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-2">
          <p className="text-[16.5px] leading-[1.75] text-mute-200">
            These are businesses where the customer journey is emotional, considered, and high-value.
            The digital layer must support trust, responsiveness, and precision long before the guest
            arrives.
          </p>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SECTORS.map((s) => (
          <article
            key={s.t}
            className="rounded-2xl glass card-hover p-6 flex flex-col gap-4"
          >
            <span className="w-10 h-10 rounded-lg bg-white/[0.025] border border-white/8 inline-flex items-center justify-center text-gold">
              <s.i className="w-4 h-4" />
            </span>
            <div className="text-[15px] text-mute-100">{s.t}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* -------------------- FEATURED PROJECTS -------------------- */
const PROJECTS = [
  {
    eyebrow: "GuestVoyages · concept",
    title: "Luxury yacht charter guest experience & operations platform.",
    body:
      "A purpose-built system connecting enquiries, guests, bookings, communications, and operational workflows into one calm, considered layer.",
    discipline: "Digital ecosystem · Operations · Guest journey",
    image:
      "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=1400&q=80",
    alt:
      "Sleek superyacht at sea — reference visual for GuestVoyages, a luxury charter operations concept.",
    href: "https://www.guestvoyages.com",
    linkLabel: "Visit GuestVoyages",
    testid: "guestvoyages-link",
    icon: Sailboat,
  },
  {
    eyebrow: "Southern Cross Blue Cruising",
    title: "Luxury yacht charter & guest experience infrastructure.",
    body:
      "An elegant digital and operational layer for blue cruising, designed around long, considered guest journeys across coastline, sea, and shore.",
    discipline: "Customer journey · Operational systems · Premium presence",
    image:
      "https://images.unsplash.com/photo-1605281317010-fe5ffe798166?auto=format&fit=crop&w=1400&q=80",
    alt:
      "A motor yacht cruising on calm, clear blue water — reference visual for Southern Cross Blue Cruising.",
    discreet: true,
    icon: Anchor,
  },
  {
    eyebrow: "Hospitality & premium service brands",
    title: "Operational clarity behind the guest experience.",
    body:
      "Considered websites, CRM, automation, and communication infrastructure for boutique hospitality, premium tourism, and high-value service brands.",
    discipline: "Websites · CRM · Automation · Discoverability",
    image:
      "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=1400&q=80",
    alt:
      "A refined boutique hotel suite at dusk — reference visual for premium hospitality systems work.",
    discreet: true,
    icon: Gem,
  },
];

function Proof() {
  return (
    <section
      id="proof"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36 scroll-mt-24"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <span className="eyebrow text-gold">Featured projects</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            Digital ecosystems behind premium guest experiences.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-[15.5px] leading-[1.75] text-mute-200">
            Each project is framed as a quiet system — websites, CRM, automation, and customer
            journeys composed into a single operational layer.
          </p>
        </div>
      </div>

      <div className="mt-14 space-y-6">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.title} project={p} index={i} />
        ))}
      </div>
    </section>
  );
}

function ProjectCard({ project, index }) {
  const Icon = project.icon;
  const reverse = index % 2 === 1;
  return (
    <article
      data-testid={`project-card-${index}`}
      className="relative rounded-3xl glass-strong overflow-hidden card-hover"
    >
      <div
        className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none opacity-80"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
        }}
      />
      <div
        className={`relative grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-2 items-stretch ${
          reverse ? "lg:[direction:rtl]" : ""
        }`}
      >
        {/* Image */}
        <div
          className={`lg:col-span-6 relative overflow-hidden aspect-[16/10] lg:aspect-auto lg:min-h-[440px] [direction:ltr]`}
        >
          <img
            src={project.image}
            alt={project.alt}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.03]"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,12,14,0.45) 0%, rgba(10,12,14,0.25) 45%, rgba(10,12,14,0.78) 100%)",
            }}
          />
          <div
            className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,166,106,0.18), transparent 70%)",
            }}
          />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
            <span className="text-[10.5px] uppercase tracking-[0.28em] text-mute-200/90">
              {project.eyebrow.split(" · ")[0]}
            </span>
            <span className="text-[10.5px] tracking-[0.18em] text-gold/85">
              {project.discreet ? "Reference visual" : "Concept"}
            </span>
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-6 p-8 sm:p-10 lg:p-14 [direction:ltr]">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/30 bg-gold/[0.06] text-gold">
              <Icon className="w-[18px] h-[18px]" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
              {project.eyebrow}
            </span>
          </div>
          <h3 className="mt-7 font-display text-[28px] sm:text-[34px] leading-[1.12] tracking-tight text-mute-100">
            {project.title}
          </h3>
          <p className="mt-5 text-[15.5px] leading-[1.75] text-mute-200 max-w-xl">
            {project.body}
          </p>
          <div className="mt-7 flex items-center gap-3 text-[12px] uppercase tracking-[0.18em] text-mute-300">
            <span className="h-px w-8 bg-gold/40" />
            <span>{project.discipline}</span>
          </div>

          {project.href && (
            <a
              href={project.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${project.linkLabel} (opens in a new tab)`}
              data-testid={project.testid}
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/[0.06] px-5 py-2.5 text-[13.5px] tracking-[0.02em] text-gold hover:border-gold hover:bg-gold/[0.12] transition-colors"
            >
              {project.linkLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

/* -------------------- FRANCHISE INFRASTRUCTURE -------------------- */
const FRANCHISE_FEATURES = [
  {
    i: Database,
    t: "Franchise CRM Systems",
    d: "A single source of truth across locations — enquiries, customers, deals, and lifecycle data, structured for multi-site visibility.",
  },
  {
    i: Inbox,
    t: "Multi-Location Lead Management",
    d: "Smart routing, ownership rules, and territory logic so every enquiry reaches the right location, in the right tone, on time.",
  },
  {
    i: Users,
    t: "Franchisee Onboarding Portals",
    d: "Quiet, branded portals that guide new franchisees through setup, brand standards, training, and first-90-day milestones.",
  },
  {
    i: GraduationCap,
    t: "SOP & Training Hubs",
    d: "A calm internal home for standard operating procedures, training material, and operational consistency across the network.",
  },
  {
    i: BarChart3,
    t: "Centralised Reporting Dashboards",
    d: "Network-wide visibility on enquiries, conversions, response times, and operational signals — designed for leadership, not noise.",
  },
  {
    i: Star,
    t: "Review & Reputation Systems",
    d: "Considered review-capture and reputation flows that protect brand quality across every location, without manual chasing.",
  },
  {
    i: Route,
    t: "Booking & Enquiry Routing",
    d: "Intelligent routing of bookings and enquiries across sites, with fallbacks, balancing, and surface-level guest awareness.",
  },
  {
    i: Sparkles,
    t: "AI-Assisted Follow-Up",
    d: "Quiet AI assistance behind the team — drafting first-touch replies, summarising threads, and prompting timely follow-ups.",
  },
  {
    i: LayoutDashboard,
    t: "Internal Operations Dashboards",
    d: "Operational dashboards for managers and HQ — designed around how teams actually run a multi-site business day-to-day.",
  },
  {
    i: Workflow,
    t: "Multi-Location Automation Workflows",
    d: "Automation that quietly removes manual handoffs across locations — onboarding, communication, reporting, and aftercare.",
  },
];

const FRANCHISE_INDUSTRIES = [
  { i: Building2, t: "Hospitality Groups" },
  { i: Utensils, t: "Restaurant Brands" },
  { i: Dumbbell, t: "Fitness Franchises" },
  { i: Stethoscope, t: "Healthcare Networks" },
  { i: Map, t: "Tourism Businesses" },
  { i: HomeIcon, t: "Estate Agency Groups" },
  { i: Gem, t: "Premium Service Brands" },
  { i: Sparkles, t: "Experience-Led Businesses" },
];

function AffordabilityTeaser() {
  return (
    <section
      data-testid="home-affordability-teaser"
      className="relative mt-24 sm:mt-36"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl glass-strong overflow-hidden p-8 sm:p-12 lg:p-14">
          <div
            className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,166,106,0.07), transparent 70%)",
            }}
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
            <div className="lg:col-span-7">
              <span className="eyebrow text-gold">— A common misconception</span>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[52px] leading-[1.06] tracking-tight text-mute-100">
                Think custom software is{" "}
                <em className="not-italic text-gradient-gold">expensive</em>?
              </h2>
              <p className="mt-6 text-[16px] leading-[1.75] text-mute-200 max-w-2xl">
                Most business owners assume bespoke systems cost tens of thousands of pounds. In
                reality, many of the platforms, portals and automations we build are achievable
                for a fraction of that investment.
              </p>
              <p className="mt-3 text-[15px] leading-[1.7] text-mute-300 max-w-2xl">
                Explore real-world examples and typical project ranges.
              </p>
            </div>

            <div className="lg:col-span-5 lg:pl-6">
              <div className="flex flex-col gap-5">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-[44px] sm:text-[52px] leading-none text-gradient-gold">
                    £795
                  </span>
                  <span className="text-[12px] uppercase tracking-[0.24em] text-mute-300">
                    from
                  </span>
                </div>
                <p className="text-[13.5px] leading-[1.65] text-mute-300 max-w-xs">
                  Typical project ranges for SMEs — built around a single business problem at a time.
                </p>
                <Link
                  to="/what-could-we-build"
                  data-testid="home-affordability-cta"
                  className="btn-primary mt-2 self-start"
                >
                  Explore examples
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FranchiseInfrastructure() {
  return (
    <section
      id="franchise"
      className="relative mt-24 sm:mt-36 scroll-mt-24"
      data-testid="franchise-infrastructure"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[-120px] w-[1100px] h-[700px] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.18] mask-fade-y grid-bg"
        />
      </div>

      {/* Header */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold">Franchise infrastructure</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] leading-[1.04] tracking-tight text-mute-100">
              Franchise growth needs more than{" "}
              <em className="not-italic text-gradient-gold">marketing</em>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] leading-[1.75] text-mute-200">
              We help ambitious businesses build the CRM systems, automation, onboarding
              infrastructure, and operational workflows needed to scale with consistency and
              control.
            </p>
          </div>
        </div>

        {/* Supporting copy */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6 lg:col-start-1">
            <div className="rounded-3xl glass p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/25 bg-gold/[0.05] text-gold">
                  <Network className="w-[18px] h-[18px]" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
                  The reality of multi-location growth
                </span>
              </div>
              <p className="mt-6 text-[15.5px] leading-[1.75] text-mute-200">
                As businesses expand into multiple locations, operational complexity increases
                quickly. Leads become fragmented, customer experiences vary, reporting becomes
                inconsistent, and onboarding becomes difficult to manage.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl glass-strong p-7 sm:p-9 relative overflow-hidden">
              <div
                className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
                }}
              />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/30 bg-gold/[0.06] text-gold">
                    <Layers className="w-[18px] h-[18px]" />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
                    Our role
                  </span>
                </div>
                <p className="mt-6 text-[15.5px] leading-[1.75] text-mute-100">
                  Linked2Marketing helps create the digital operational layer behind scalable
                  growth — connecting enquiries, onboarding, automation, reporting, customer
                  communication, and internal workflows into one cleaner ecosystem.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Feature grid */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <h3 className="font-display text-2xl sm:text-3xl text-mute-100 leading-[1.15] max-w-2xl">
              The operational layer beneath scalable, multi-location growth.
            </h3>
            <p className="text-[12px] uppercase tracking-[0.22em] text-mute-300">
              10 connected modules · designed as one system
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {FRANCHISE_FEATURES.map((f, i) => (
              <article
                key={f.t}
                data-testid={`franchise-card-${i}`}
                className="group relative rounded-3xl glass card-hover p-6 sm:p-7 overflow-hidden h-full"
              >
                <div
                  className="absolute -top-16 -right-16 w-[220px] h-[220px] rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
                  }}
                />
                <div className="relative flex flex-col h-full">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-gold/25 bg-gold/[0.05] text-gold transition-transform duration-500 group-hover:-translate-y-0.5">
                    <f.i className="w-[18px] h-[18px]" />
                  </span>
                  <div className="mt-6 flex items-baseline gap-3">
                    <span className="text-[10.5px] tabular-nums tracking-[0.24em] text-gold/80">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px w-8 bg-gold/30" />
                  </div>
                  <h4 className="mt-3 font-display text-[19px] sm:text-[20px] leading-[1.2] text-mute-100">
                    {f.t}
                  </h4>
                  <p className="mt-3 text-[14px] leading-[1.65] text-mute-200">{f.d}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Designed for scalable brands */}
        <div className="mt-24 sm:mt-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <span className="eyebrow text-gold">Designed for scalable brands</span>
              <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-mute-100">
                Quiet infrastructure for businesses built to grow.
              </h3>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[15.5px] leading-[1.75] text-mute-200">
                We work with brands where consistency, customer experience, and operational
                clarity matter at every location — across hospitality, tourism, healthcare,
                fitness, premium services, and experience-led industries.
              </p>
            </div>
          </div>

          <ul className="mt-10 flex flex-wrap gap-3">
            {FRANCHISE_INDUSTRIES.map((ind, i) => (
              <li
                key={ind.t}
                data-testid={`franchise-industry-${i}`}
                className="group inline-flex items-center gap-2.5 rounded-full px-4 py-2.5 text-[13.5px] tracking-[0.01em] text-mute-100 border border-white/8 bg-white/[0.025] backdrop-blur-md hover:border-gold/40 hover:text-gold hover:bg-gold/[0.05] transition-colors"
              >
                <ind.i className="w-3.5 h-3.5 text-gold/80 group-hover:text-gold transition-colors" />
                {ind.t}
              </li>
            ))}
          </ul>
        </div>

        {/* Positioning statement */}
        <figure className="mt-24 sm:mt-28">
          <div className="relative rounded-3xl glass-strong overflow-hidden p-10 sm:p-14 lg:p-20">
            <div
              className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
              }}
            />
            <div
              className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
              }}
            />
            <div className="relative max-w-4xl">
              <span className="text-gold text-5xl leading-none font-display select-none">
                &ldquo;
              </span>
              <blockquote className="mt-2 font-display text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.18] tracking-tight text-mute-100">
                Modern franchise growth depends on{" "}
                <em className="not-italic text-gradient-gold">systems, consistency, visibility,</em>{" "}
                and operational infrastructure — not just marketing.
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-mute-300">
                <span className="h-px w-10 bg-gold/40" />
                Linked2Marketing · perspective
              </figcaption>
            </div>
          </div>
        </figure>

        {/* CTA sub-section */}
        <div className="mt-20 sm:mt-24">
          <div className="relative rounded-3xl glass overflow-hidden p-10 sm:p-14">
            <div
              className="absolute -top-24 right-1/4 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-8">
                <span className="eyebrow text-gold">Before complexity arrives</span>
                <h3 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-mute-100">
                  Build the infrastructure before growth gets messy.
                </h3>
                <p className="mt-5 text-[15.5px] leading-[1.75] text-mute-200 max-w-2xl">
                  Whether you operate two locations or twenty, scalable growth requires cleaner
                  systems behind the scenes.
                </p>
              </div>
              <div className="lg:col-span-4 lg:justify-self-end">
                <Link
                  to="/#enquiry"
                  data-testid="franchise-cta-explore"
                  className="btn-primary"
                >
                  Explore Franchise Infrastructure
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <p className="mt-4 text-[12px] text-mute-300 tracking-[0.01em] lg:text-right">
                  A considered conversation · not a sales sequence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* -------------------- HOSPITALITY OPERATIONS & SYSTEMS -------------------- */
const HOSPITALITY_AREAS = [
  { i: Utensils, t: "F&B Operational Reviews" },
  { i: ClipboardList, t: "Stock, Purchasing & Margin Control" },
  { i: Users, t: "Labour & Productivity" },
  { i: Workflow, t: "Operational Systems & Processes" },
  { i: BarChart3, t: "Management Information & Reporting" },
  { i: TrendingUp, t: "Customer Journey & Revenue Opportunities" },
  { i: LayoutDashboard, t: "Digital Transformation" },
  { i: Sparkles, t: "Automation & AI" },
  { i: CalendarCheck, t: "Peak Period & Event Planning" },
];

function HospitalityOperations() {
  return (
    <section
      id="hospitality"
      data-testid="home-hospitality-operations"
      className="relative mt-24 sm:mt-36 scroll-mt-24"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[-120px] w-[1100px] h-[700px] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold">Hospitality operations &amp; systems</span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.06] tracking-tight text-mute-100">
              35+ years of hospitality experience.{" "}
              <em className="not-italic text-gradient-gold">
                A different perspective today.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16px] leading-[1.75] text-mute-200">
              Before Linked2Marketing, Gavin Patterson spent more than three decades owning and
              operating restaurants, bars and hotels.
            </p>
            <p className="mt-4 text-[15.5px] leading-[1.7] text-mute-200">
              Today, that hands-on operational experience is combined with process analysis,
              digital systems, automation and AI.
            </p>
            <p className="mt-4 text-[15px] leading-[1.7] text-mute-300">
              We help hospitality and leisure businesses identify where operations are losing
              time, money or control, then develop practical ways to improve them.
            </p>
          </div>
        </div>

        {/* Areas of support */}
        <div className="mt-14 rounded-3xl glass p-7 sm:p-10 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
            }}
          />
          <div className="relative flex items-end justify-between gap-6 flex-wrap">
            <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
              Areas of support
            </span>
            <span className="text-[11px] uppercase tracking-[0.22em] text-mute-300">
              Hotels · Restaurants · Bars · Resorts · Attractions · Event venues
            </span>
          </div>
          <ul className="relative mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {HOSPITALITY_AREAS.map((a) => {
              const Icon = a.i;
              return (
                <li
                  key={a.t}
                  className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-mute-100"
                >
                  <span className="mt-[2px] inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gold/25 bg-gold/[0.05] text-gold flex-none">
                    <Icon className="w-[14px] h-[14px]" />
                  </span>
                  <span>{a.t}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Ending copy + CTA */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-[16px] leading-[1.75] text-mute-200 max-w-2xl">
              Whether the problem is operational, commercial or technological, we start by
              understanding how the business actually works.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/hospitality-consulting"
              data-testid="home-hospitality-cta"
              className="btn-primary inline-flex"
            >
              Explore hospitality consulting
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


/* -------------------- WHY IT MATTERS -------------------- */
function WhyItMatters() {
  const items = [
    { i: Clock3, t: "Faster, clearer responses across the team" },
    { i: MessageSquareQuote, t: "More consistent guest and customer communication" },
    { i: Eye, t: "Operational visibility across enquiries, bookings, and aftercare" },
    { i: Gem, t: "A more refined, considered experience from first touchpoint onwards" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <span className="eyebrow text-gold">Why it matters</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-mute-100">
            When the digital layer works, the service feels effortless.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-[15.5px] leading-[1.75] text-mute-200">
            The most refined systems are invisible to the customer. They simply make the experience
            feel smoother, more personal, and quietly assured.
          </p>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div
            key={it.t}
            className="rounded-2xl glass card-hover p-6 sm:p-7 flex items-start gap-4"
          >
            <span className="w-10 h-10 rounded-lg bg-white/[0.025] border border-white/8 inline-flex items-center justify-center text-gold flex-none">
              <it.i className="w-4 h-4" />
            </span>
            <p className="text-[15.5px] text-mute-100 leading-[1.55]">{it.t}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- FINAL CTA -------------------- */
function FinalCta() {
  return (
    <section
      data-testid="final-cta"
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-24 sm:mt-36"
    >
      <div className="relative overflow-hidden rounded-3xl glass-strong p-10 sm:p-16 lg:p-20">
        <div
          className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.18), transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-32 -left-24 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.08), transparent 70%)",
          }}
        />
        <div className="absolute inset-0 grid-bg opacity-[0.16] mask-fade-y" />

        <div className="relative max-w-3xl">
          <span className="eyebrow text-gold">Next step</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-mute-100">
            A calmer, more capable digital layer behind your service.
          </h2>
          <p className="mt-6 text-[16.5px] leading-[1.7] text-mute-200 max-w-2xl">
            If your website, CRM, bookings, and communication no longer feel connected,
            Linked2Marketing can help you design the operational systems that quietly support an
            exceptional customer experience.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#enquiry"
              data-testid="final-cta-book"
              className="btn-primary"
            >
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-6 text-[13px] text-mute-300 max-w-xl">
            Written reply within one business day · No automated sales sequence · No generic audit.
          </p>
        </div>
      </div>
    </section>
  );
}
